import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { RoleBasedNavigation } from '@/components/RoleBasedNavigation';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { 
  MessageSquare, 
  AlertCircle, 
  CheckCircle, 
  Clock, 
  Star,
  Filter,
  Search,
  Plus,
  Eye,
  Edit,
  TrendingUp,
  Users,
  FileText,
  Calendar,
  User,
  Kanban,
  List,
  Tag,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { apiService } from '@/services/api';
import ClientSelector from '@/components/ClientSelector';
import { FeedbackActivityHistory } from '@/components/FeedbackActivityHistory';

interface Feedback {
  id: number;
  title: string;
  description: string;
  section_id: number;
  section?: {
    id: number;
    name: string;
    color: string;
  };
  status: string;
  priority: string;
  category?: string;
  tags: string[];
  admin_notes?: string;
  createdAt: string;
  updatedAt: string;
  client: {
    id: number;
    legal_name: string;
    trade_name: string;
  };
  user: {
    id: number;
    name: string;
    email: string;
  };
}

interface FeedbackStats {
  status: Record<string, number>;
  priority: Record<string, number>;
  type: Record<string, number>;
  total: number;
}

interface Client {
  id: number;
  legal_name: string;
  trade_name: string;
  account_id?: number;
}

const ClientSuccess = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [stats, setStats] = useState<FeedbackStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [chamadoToDelete, setChamadoToDelete] = useState<Feedback | null>(null);
  const [filters, setFilters] = useState({
    status: 'all',
    priority: 'all',
    feedback_type: 'all',
    search: '',
    client_id: 'all',
    section_id: 'all',
    start_date: '',
    end_date: ''
  });
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const [viewMode, setViewMode] = useState<'list' | 'board'>('list');
  const [clients, setClients] = useState<Client[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [isCreateSectionOpen, setIsCreateSectionOpen] = useState(false);
  const [sectionForm, setSectionForm] = useState({
    name: '',
    description: '',
    color: '#3B82F6'
  });

  const [updateForm, setUpdateForm] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    admin_notes: ''
  });

  // Estados para cadastro de feedback
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [feedbackForm, setFeedbackForm] = useState({
    title: '',
    description: '',
    section_id: 1, // Sessão padrão (Geral)
    priority: 'medium',
    category: '',
    tags: ''
  });

  useEffect(() => {
    loadFeedbacks();
    loadStats();
    loadClients();
    loadSections();
  }, [pagination.page, filters]);

  const loadFeedbacks = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString()
      });

      // Adicionar filtros apenas se não forem 'all' ou vazios
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all' && key !== 'start_date' && key !== 'end_date') {
          params.append(key, value);
        }
      });

      // Adicionar filtros de data separadamente
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }

      // Adicionar ordenação
      params.append('sort_order', sortOrder);

      // Usar rota diferente baseada no tipo de usuário
      const endpoint = user?.role === 'admin' ? '/feedback/all' : '/feedback/client/feedbacks';
      const response = await apiService.get(`${endpoint}?${params}`);
      
      if (response && response.feedbacks) {
        setFeedbacks(response.feedbacks);
        setPagination(prev => ({
          ...prev,
          total: response.total || 0,
          totalPages: Math.ceil((response.total || 0) / pagination.limit)
        }));
      } else {
        setFeedbacks([]);
        setPagination(prev => ({
          ...prev,
          total: 0,
          totalPages: 0
        }));
      }
    } catch (error) {
      console.error('Erro ao carregar feedbacks:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar feedbacks",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      // Apenas admins podem ver estatísticas
      if (user?.role !== 'admin') {
        setStats({
          status: {},
          priority: {},
          type: {},
          total: 0
        });
        return;
      }

      const response = await apiService.get('/feedback/stats');
      
      if (response && response.success && response.stats) {
        setStats(response.stats);
      } else {
        // Fallback para estatísticas vazias
        setStats({
          status: {},
          priority: {},
          type: {},
          total: 0
        });
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      // Fallback para estatísticas vazias em caso de erro
      setStats({
        status: {},
        priority: {},
        type: {},
        total: 0
      });
    }
  };

  const loadClients = async () => {
    try {
      const clients = await apiService.getClients();
      setClients(clients);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      setClients([]);
    }
  };

  const loadSections = async () => {
    try {
      const response = await apiService.get('/feedback-sections/sections');
      setSections(response.sections || []);
    } catch (error) {
      console.error('Erro ao carregar seções:', error);
      setSections([]);
    }
  };

  const handleCreateSection = async () => {
    if (!sectionForm.name.trim()) {
      toast({
        title: "Erro",
        description: "Nome da sessão é obrigatório",
        variant: "destructive",
      });
      return;
    }

    try {
      await apiService.post('/feedback-sections/sections', sectionForm);
      toast({
        title: "Sucesso",
        description: "Sessão criada com sucesso"
      });
      setIsCreateSectionOpen(false);
      setSectionForm({ name: '', description: '', color: '#3B82F6' });
      loadSections();
    } catch (error: any) {
      toast({
        title: "Erro",
                  description: error.response?.data?.error || "Erro ao criar sessão",
        variant: "destructive",
      });
    }
  };

  const handleUpdateFeedback = async () => {
    if (!selectedFeedback) return;

    try {
      await apiService.put(`/feedback/${selectedFeedback.id}`, updateForm);
      toast({
        title: "Sucesso",
        description: "Chamado atualizado com sucesso"
      });
      setIsUpdateOpen(false);
      loadFeedbacks();
      loadStats();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao atualizar chamado",
        variant: "destructive"
      });
    }
  };

  const handleDeleteFeedback = async () => {
    if (!chamadoToDelete) return;

    try {
      await apiService.delete(`/feedback/${chamadoToDelete.id}`);
      
      toast({
        title: "Sucesso",
        description: "Chamado excluído com sucesso!"
      });

      setIsDeleteOpen(false);
      setChamadoToDelete(null);
      loadFeedbacks();
      loadStats();
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao excluir chamado",
        variant: "destructive"
      });
    }
  };

  const handleCreateFeedback = async () => {
    if (!selectedClient) {
      toast({
        title: "Erro",
        description: "Selecione um cliente primeiro",
        variant: "destructive"
      });
      return;
    }

    if (!feedbackForm.title || !feedbackForm.description) {
      toast({
        title: "Erro",
        description: "Título e descrição são obrigatórios",
        variant: "destructive"
      });
      return;
    }

    try {
      await apiService.post('/feedback', {
        ...feedbackForm,
        client_id: selectedClient.id,
        section_id: feedbackForm.section_id,
        tags: feedbackForm.tags ? feedbackForm.tags.split(',').map(tag => tag.trim()) : []
      });

      toast({
        title: "Sucesso",
        description: "Chamado cadastrado com sucesso!"
      });

      // Limpar formulário
      setFeedbackForm({
        title: '',
        description: '',
        section_id: 1,
        priority: 'medium',
        category: '',
        tags: ''
      });

      // Recarregar feedbacks
      loadFeedbacks();
      loadStats();
      
      // Fechar modal
      setIsCreateOpen(false);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao cadastrar chamado",
        variant: "destructive"
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_review': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-orange-100 text-orange-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'critical': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Data não disponível';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('Data inválida:', dateString);
        return 'Data inválida';
      }
      
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', dateString, error);
      return 'Data inválida';
    }
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      priority: 'all',
      feedback_type: 'all',
      search: '',
      client_id: 'all',
      section_id: 'all',
      start_date: '',
      end_date: ''
    });
  };

  // Agrupar feedbacks por status para o board
  const getFeedbacksByStatus = () => {
    const grouped = {
      pending: feedbacks.filter(f => f.status === 'pending'),
      in_review: feedbacks.filter(f => f.status === 'in_review'),
      in_progress: feedbacks.filter(f => f.status === 'in_progress'),
      resolved: feedbacks.filter(f => f.status === 'resolved'),
      closed: feedbacks.filter(f => f.status === 'closed')
    };
    return grouped;
  };

  const translateStatus = (status: string) => {
    const translations = {
      pending: 'Pendente',
      in_review: 'Em Revisão',
      in_progress: 'Em Progresso',
      resolved: 'Resolvido',
      closed: 'Fechado'
    };
    return translations[status as keyof typeof translations] || status;
  };

  const translatePriority = (priority: string) => {
    const translations = {
      low: 'Baixa',
      medium: 'Média',
      high: 'Alta',
      critical: 'Crítica'
    };
    return translations[priority as keyof typeof translations] || priority;
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      pending: { title: 'Pendentes', color: 'bg-yellow-50 border-yellow-200', icon: Clock },
      in_review: { title: 'Em Revisão', color: 'bg-blue-50 border-blue-200', icon: AlertCircle },
      in_progress: { title: 'Em Progresso', color: 'bg-orange-50 border-orange-200', icon: AlertCircle },
      resolved: { title: 'Resolvidos', color: 'bg-green-50 border-green-200', icon: CheckCircle },
      closed: { title: 'Fechados', color: 'bg-gray-50 border-gray-200', icon: CheckCircle }
    };
    return configs[status as keyof typeof configs] || configs.pending;
  };

  return (
    <RoleBasedNavigation allowedRoles={['admin']}>
      <Layout>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Sucesso do Cliente</h1>
              <p className="text-gray-600 mt-2">Gerencie chamados e sugestões dos clientes</p>
            </div>
            <div className="flex items-center space-x-2">
              <MessageSquare className="w-6 h-6 text-primary-600" />
                              <span className="text-lg font-semibold text-gray-900">
                  {stats?.total || 0} chamados
                </span>
            </div>
          </div>

          {/* Botões de Ação */}
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="flex items-center space-x-2"
              >
                <List className="w-4 h-4" />
                <span>Lista</span>
              </Button>
              <Button
                variant={viewMode === 'board' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('board')}
                className="flex items-center space-x-2"
              >
                <Kanban className="w-4 h-4" />
                <span>Board</span>
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <Button 
                onClick={() => setIsCreateSectionOpen(true)}
                variant="outline"
                className="flex items-center space-x-2"
              >
                <Tag className="w-4 h-4" />
                <span>Cadastrar Sessão</span>
              </Button>
              <Button 
                onClick={() => setIsCreateOpen(true)}
                className="flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Cadastrar Feedback</span>
              </Button>
            </div>
          </div>

          {/* Conteúdo Principal */}
          <div className="space-y-6">
              {/* Estatísticas */}
              {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <Clock className="w-5 h-5 text-yellow-600" />
                        <div>
                          <p className="text-sm text-gray-600">Pendentes</p>
                          <p className="text-2xl font-bold">{stats.status.pending || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                        <div>
                          <p className="text-sm text-gray-600">Em Progresso</p>
                          <p className="text-2xl font-bold">{stats.status.in_progress || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm text-gray-600">Resolvidos</p>
                          <p className="text-2xl font-bold">{stats.status.resolved || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="text-sm text-gray-600">Total</p>
                          <p className="text-2xl font-bold">{stats.total}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Filtros */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Filter className="w-5 h-5" />
                    <span>Filtros</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-4 items-end">
                    <div className="flex-1 min-w-[200px]">
                      <label className="text-sm font-medium mb-2 block">Buscar</label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <Input
                          placeholder="Buscar por título, descrição..."
                          value={filters.search}
                          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Status</label>
                      <Select value={filters.status} onValueChange={(value) => setFilters({ ...filters, status: value })}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="pending">Pendente</SelectItem>
                          <SelectItem value="in_review">Em Revisão</SelectItem>
                          <SelectItem value="in_progress">Em Progresso</SelectItem>
                          <SelectItem value="resolved">Resolvido</SelectItem>
                          <SelectItem value="closed">Fechado</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Prioridade</label>
                      <Select value={filters.priority} onValueChange={(value) => setFilters({ ...filters, priority: value })}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas</SelectItem>
                          <SelectItem value="low">Baixa</SelectItem>
                          <SelectItem value="medium">Média</SelectItem>
                          <SelectItem value="high">Alta</SelectItem>
                          <SelectItem value="critical">Crítica</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Sessão</label>
                      <Select value={filters.section_id} onValueChange={(value) => setFilters({ ...filters, section_id: value })}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todas as sessões</SelectItem>
                          {sections.filter(section => section.id && section.id.toString().trim() !== '').map((section) => (
                            <SelectItem key={section.id} value={section.id.toString()}>
                              {section.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Cliente</label>
                      <Select 
                        value={filters.client_id} 
                        onValueChange={(value) => setFilters({ ...filters, client_id: value })}
                      >
                        <SelectTrigger className="w-[200px]">
                          <SelectValue placeholder="Todos os clientes" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos os clientes</SelectItem>
                          {clients.filter(client => client.id && client.id.toString().trim() !== '').map((client) => (
                            <SelectItem key={client.id} value={client.id.toString()}>
                              {client.trade_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Data Inicial</label>
                      <Input
                        type="date"
                        value={filters.start_date}
                        onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                        className="w-[150px]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Data Final</label>
                      <Input
                        type="date"
                        value={filters.end_date}
                        onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                        className="w-[150px]"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-2 block">Ordenar</label>
                      <Select value={sortOrder} onValueChange={(value: 'desc' | 'asc') => setSortOrder(value)}>
                        <SelectTrigger className="w-[150px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="desc">Mais Recentes</SelectItem>
                          <SelectItem value="asc">Mais Antigos</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button onClick={clearFilters} variant="outline" className="flex items-center space-x-2">
                      <Filter className="w-4 h-4" />
                      <span>Limpar</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Lista de Feedbacks */}
              {viewMode === 'list' ? (
                <Card>
                  <CardHeader>
                                            <CardTitle>Chamados dos Clientes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                                                  <span className="ml-2">Carregando chamados...</span>
                      </div>
                    ) : feedbacks.length === 0 ? (
                      <div className="text-center py-12">
                        <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum chamado encontrado</h3>
                          <p className="text-gray-600">Não há chamados que correspondam aos filtros aplicados</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {feedbacks.map((feedback) => (
                          <div key={feedback.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h3 className="font-semibold text-lg">{feedback.title}</h3>
                                  <Badge className={getStatusColor(feedback.status)}>
                                    {translateStatus(feedback.status)}
                                  </Badge>
                                  <Badge className={getPriorityColor(feedback.priority)}>
                                    {translatePriority(feedback.priority)}
                                  </Badge>
                                </div>
                                <p className="text-gray-600 mb-3 line-clamp-2">{feedback.description}</p>
                                <div className="flex items-center space-x-4 text-sm text-gray-500">
                                  <span className="flex items-center space-x-1">
                                    <User className="w-4 h-4" />
                                    <span>{feedback.client.trade_name}</span>
                                  </span>
                                  <span className="flex items-center space-x-1">
                                    <Calendar className="w-4 h-4" />
                                    <span>{formatDate(feedback.createdAt)}</span>
                                  </span>
                                  <span className="flex items-center space-x-1">
                                    <Tag className="w-4 h-4" />
                                    <span className="font-medium">{feedback.section?.name || 'Geral'}</span>
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedFeedback(feedback);
                                    setIsDetailOpen(true);
                                  }}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedFeedback(feedback);
                                    setUpdateForm({
                                      title: feedback.title,
                                      description: feedback.description,
                                      status: feedback.status,
                                      priority: feedback.priority,
                                      admin_notes: feedback.admin_notes || ''
                                    });
                                    setIsUpdateOpen(true);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => {
                                    console.log('Botão de excluir clicado para feedback:', feedback.id);
                                    setChamadoToDelete(feedback);
                                    setIsDeleteOpen(true);
                                  }}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                /* Board de Feedbacks (Trello) */
                <div className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Kanban className="w-5 h-5" />
                        <span>Board de Chamados</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {loading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                          <span className="ml-2">Carregando chamados...</span>
                        </div>
                      ) : feedbacks.length === 0 ? (
                        <div className="text-center py-12">
                          <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum chamado encontrado</h3>
                          <p className="text-gray-600">Não há chamados que correspondam aos filtros aplicados</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                          {Object.entries(getFeedbacksByStatus()).map(([status, statusFeedbacks]) => {
                            const config = getStatusConfig(status);
                            const IconComponent = config.icon;
                            
                            return (
                              <div key={status} className={`rounded-lg border-2 ${config.color} min-h-[500px]`}>
                                <div className="p-4 border-b border-gray-200">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <IconComponent className="w-4 h-4" />
                                      <h3 className="font-semibold text-sm">{config.title}</h3>
                                    </div>
                                    <Badge variant="secondary" className="text-xs">
                                      {statusFeedbacks.length}
                                    </Badge>
                                  </div>
                                </div>
                                <div className="p-2 space-y-2 max-h-[400px] overflow-y-auto">
                                  {statusFeedbacks.map((feedback) => (
                                    <div
                                      key={feedback.id}
                                      className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                                      onClick={() => {
                                        setSelectedFeedback(feedback);
                                        setIsDetailOpen(true);
                                      }}
                                    >
                                      <div className="space-y-2">
                                        <div className="flex items-start justify-between">
                                          <h4 className="font-medium text-sm line-clamp-2">{feedback.title}</h4>
                                          <Badge className={`text-xs ${getPriorityColor(feedback.priority)}`}>
                                            {translatePriority(feedback.priority)}
                                          </Badge>
                                        </div>
                                        <p className="text-xs text-gray-600 line-clamp-3">{feedback.description}</p>
                                        <div className="space-y-1">
                                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                                            <User className="w-3 h-3" />
                                            <span className="truncate">{feedback.client.trade_name || feedback.client.legal_name}</span>
                                          </div>
                                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                                            <Calendar className="w-3 h-3" />
                                            <span>{formatDate(feedback.createdAt)}</span>
                                          </div>
                                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                                            <Tag className="w-3 h-3" />
                                            <span className="font-medium">{feedback.section?.name || 'Geral'}</span>
                                          </div>
                                        </div>
                                        <div className="flex justify-end space-x-1 pt-2">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedFeedback(feedback);
                                              setUpdateForm({
                                                title: feedback.title,
                                                description: feedback.description,
                                                status: feedback.status,
                                                priority: feedback.priority,
                                                admin_notes: feedback.admin_notes || ''
                                              });
                                              setIsUpdateOpen(true);
                                            }}
                                          >
                                            <Edit className="w-3 h-3" />
                                          </Button>
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="h-6 px-2 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setChamadoToDelete(feedback);
                                              setIsDeleteOpen(true);
                                            }}
                                          >
                                            <Trash2 className="w-3 h-3" />
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                  {statusFeedbacks.length === 0 && (
                                    <div className="text-center py-8 text-gray-400">
                                      <MessageSquare className="w-8 h-8 mx-auto mb-2" />
                                      <p className="text-xs">Nenhum chamado</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>

          {/* Modal de Cadastro */}
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogContent className="max-w-4xl">
              <DialogHeader>
                <DialogTitle>Cadastrar Novo Chamado</DialogTitle>
                <DialogDescription>
                  Selecione um cliente e preencha os dados do chamado
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Seleção de Cliente */}
                <ClientSelector 
                  onClientSelect={setSelectedClient}
                  selectedClient={selectedClient}
                />

                {/* Formulário de Feedback */}
                {selectedClient && (
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium">Título *</label>
                      <Input
                        value={feedbackForm.title}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, title: e.target.value })}
                        placeholder="Resumo do feedback"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm font-medium">Descrição *</label>
                      <Textarea
                        value={feedbackForm.description}
                        onChange={(e) => setFeedbackForm({ ...feedbackForm, description: e.target.value })}
                        placeholder="Descreva detalhadamente o feedback..."
                        rows={4}
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Sessão</label>
                        <Select 
                          value={feedbackForm.section_id.toString()} 
                          onValueChange={(value) => setFeedbackForm({ ...feedbackForm, section_id: parseInt(value) })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {sections.filter(section => section.id && section.id.toString().trim() !== '').map((section) => (
                              <SelectItem key={section.id} value={section.id.toString()}>
                                {section.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <label className="text-sm font-medium">Prioridade</label>
                        <Select 
                          value={feedbackForm.priority} 
                          onValueChange={(value) => setFeedbackForm({ ...feedbackForm, priority: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Baixa</SelectItem>
                            <SelectItem value="medium">Média</SelectItem>
                            <SelectItem value="high">Alta</SelectItem>
                            <SelectItem value="critical">Crítica</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Categoria</label>
                        <Input
                          value={feedbackForm.category}
                          onChange={(e) => setFeedbackForm({ ...feedbackForm, category: e.target.value })}
                          placeholder="Ex: Interface, Performance, Segurança"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium">Tags</label>
                        <Input
                          value={feedbackForm.tags}
                          onChange={(e) => setFeedbackForm({ ...feedbackForm, tags: e.target.value })}
                          placeholder="Ex: mobile, dashboard, relatórios"
                        />
                        <p className="text-xs text-gray-500 mt-1">Separe as tags por vírgula</p>
                      </div>
                    </div>
                  </div>
                )}

                {!selectedClient && (
                  <div className="col-span-2 text-center py-8">
                    <MessageSquare className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Selecione um Cliente</h3>
                    <p className="text-gray-600">
                      Escolha um cliente no painel esquerdo para começar a cadastrar feedbacks
                    </p>
                  </div>
                )}

                <div className="col-span-2 flex justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleCreateFeedback}
                    disabled={!selectedClient || !feedbackForm.title || !feedbackForm.description}
                    className="flex items-center space-x-2"
                  >
                    <MessageSquare className="w-4 h-4" />
                    <span>Cadastrar Feedback</span>
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Modal de Detalhes */}
          <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Detalhes do Feedback</DialogTitle>
              </DialogHeader>
              {selectedFeedback && (
                <div className="space-y-6">
                  {/* Detalhes do Feedback */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-lg">{selectedFeedback.title}</h3>
                      <p className="text-gray-600 mt-2">{selectedFeedback.description}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Cliente</p>
                        <p className="text-sm">{selectedFeedback.client.trade_name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Usuário</p>
                        <p className="text-sm">{selectedFeedback.user.name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Status</p>
                        <Badge className={getStatusColor(selectedFeedback.status)}>
                          {translateStatus(selectedFeedback.status)}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Prioridade</p>
                        <Badge className={getPriorityColor(selectedFeedback.priority)}>
                          {translatePriority(selectedFeedback.priority)}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Sessão</p>
                        <Badge variant="outline" className="flex items-center space-x-1 w-fit">
                          <Tag className="w-3 h-3" />
                          <span>{selectedFeedback.section?.name || 'Geral'}</span>
                        </Badge>
                      </div>
                    </div>
                    {selectedFeedback.admin_notes && (
                      <div>
                        <p className="text-sm font-medium text-gray-600">Notas do Admin</p>
                        <p className="text-sm bg-gray-50 p-3 rounded">{selectedFeedback.admin_notes}</p>
                      </div>
                    )}
                    <div className="text-sm text-gray-500">
                      Criado em: {formatDate(selectedFeedback.createdAt)}
                    </div>
                  </div>

                  {/* Separador */}
                  <div className="border-t border-gray-200"></div>

                  {/* Histórico de Atividades */}
                  <FeedbackActivityHistory 
                    feedbackId={selectedFeedback.id}
                    onActivityAdded={() => {
                      // Recarregar feedbacks se necessário
                      loadFeedbacks();
                    }}
                  />
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Modal de Atualização */}
          <Dialog open={isUpdateOpen} onOpenChange={setIsUpdateOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Atualizar Chamado</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Título *</label>
                  <Input
                    value={updateForm.title}
                    onChange={(e) => setUpdateForm({ ...updateForm, title: e.target.value })}
                    placeholder="Título do chamado"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Descrição *</label>
                  <Textarea
                    value={updateForm.description}
                    onChange={(e) => setUpdateForm({ ...updateForm, description: e.target.value })}
                    placeholder="Descrição do chamado"
                    rows={4}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <Select value={updateForm.status} onValueChange={(value) => setUpdateForm({ ...updateForm, status: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="in_review">Em Revisão</SelectItem>
                      <SelectItem value="in_progress">Em Progresso</SelectItem>
                      <SelectItem value="resolved">Resolvido</SelectItem>
                      <SelectItem value="closed">Fechado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Prioridade</label>
                  <Select value={updateForm.priority} onValueChange={(value) => setUpdateForm({ ...updateForm, priority: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Baixa</SelectItem>
                      <SelectItem value="medium">Média</SelectItem>
                      <SelectItem value="high">Alta</SelectItem>
                      <SelectItem value="critical">Crítica</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Notas do Admin</label>
                  <Textarea
                    value={updateForm.admin_notes}
                    onChange={(e) => setUpdateForm({ ...updateForm, admin_notes: e.target.value })}
                    placeholder="Adicione notas sobre o feedback..."
                    rows={4}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setIsUpdateOpen(false)}>
                    Cancelar
                  </Button>
                  <Button onClick={handleUpdateFeedback}>
                    Atualizar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Modal de Confirmação de Exclusão */}
          <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogDescription>
                  Tem certeza que deseja excluir o chamado "{chamadoToDelete?.title}"? Esta ação não pode ser desfeita.
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setIsDeleteOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDeleteFeedback}
                >
                  Excluir
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Modal de Cadastro de Sessão */}
          <Dialog open={isCreateSectionOpen} onOpenChange={setIsCreateSectionOpen}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Nova Sessão</DialogTitle>
                <DialogDescription>
                  Adicione uma nova sessão para organizar os chamados
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Nome *</label>
                  <Input
                    value={sectionForm.name}
                    onChange={(e) => setSectionForm({ ...sectionForm, name: e.target.value })}
                    placeholder="Ex: Mobile, Dashboard, Relatórios"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Descrição</label>
                  <Textarea
                    value={sectionForm.description}
                    onChange={(e) => setSectionForm({ ...sectionForm, description: e.target.value })}
                    placeholder="Descreva o propósito desta sessão"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Cor</label>
                  <div className="grid grid-cols-7 gap-2 mt-2">
                    {[
                      '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#84CC16',
                      '#F97316', '#EC4899', '#6366F1', '#14B8A6', '#F43F5E', '#A855F7', '#0EA5E9'
                    ].map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={`w-8 h-8 rounded-full border-2 ${
                          sectionForm.color === color ? 'border-gray-800' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                        onClick={() => setSectionForm({ ...sectionForm, color })}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setIsCreateSectionOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateSection} disabled={!sectionForm.name.trim()}>
                                      Criar Sessão
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </Layout>
    </RoleBasedNavigation>
  );
};

export default ClientSuccess;
